// AffiliatePortal.jsx - What an affiliate has sent us, and what it earned them.
//
// The way in has no password. They prove they still hold the email address or
// the phone number we already have, which is what a password reset proves and
// one step shorter, and the browser keeps the pass so they do not do it twice.
//
// The page is the answer to one question somebody asks every few weeks: has
// anything I sent you come good yet. So the number they are owed is the largest
// thing on it, their link is one tap from being shared again, and everything
// else is underneath.
//
// `noindex`, because a search result for this page is a stranger landing on
// somebody else's earnings screen and finding a sign in form. Nothing here is
// public even though the route is.
//
// LEGEND
//   1. AffiliatePortal  the page, signed in or not
//   2. SignIn           email a link, or text a code
//   3. Dashboard        the code, the link, the money, the orders

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Seo } from "../lib/seo.jsx";
import { portalApi } from "../lib/api.js";
import { useForm } from "../lib/hooks.js";
import { rememberAffiliate, rememberedAffiliate, forgetAffiliate } from "../lib/session.js";
// moneyExact, not money. This page exists so somebody can check what they are
// owed against what arrived, and the rounded formatter shows $640.50 as $641,
// which is a phone call about fifty cents.
import { moneyExact, day, ago } from "../lib/format.js";
import { Button, Input, Alert, Panel, Loading, Tag, Stat, CopyButton, Empty } from "../ui/kit.jsx";
import { Envelope, Message, ArrowRight, Phone, Clock } from "../ui/icons.jsx";
import { Reveal } from "../ui/motion.jsx";

////////// 1. THE PAGE //////////

export default function AffiliatePortal() {
  const [params, setParams] = useSearchParams();
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);
  const [state, setState] = useState("starting");

  // Arriving from the emailed link, or coming back to a browser that already
  // knows. Read after mount, never during render: this page is prerendered and
  // localStorage does not exist at that point.
  useEffect(() => {
    const fromLink = params.get("t");
    const kept = fromLink || rememberedAffiliate();
    if (!kept) {
      setState("out");
      return;
    }
    if (fromLink) {
      rememberAffiliate(fromLink);
      // Out of the address bar. A pass in a URL is a pass in the history, in a
      // bookmark, and in whatever the next link they click is told about where
      // they came from.
      setParams({}, { replace: true });
    }
    setToken(kept);
  }, [params, setParams]);

  useEffect(() => {
    if (!token) return;
    setState("loading");
    portalApi.me(token)
      .then((answer) => { setData(answer); setState("in"); })
      .catch(() => {
        // Thirty days is up, or it was revoked. Nothing to explain: ask again.
        forgetAffiliate();
        setToken(null);
        setState("out");
      });
  }, [token]);

  const signedIn = (pass) => {
    rememberAffiliate(pass);
    setToken(pass);
  };

  const signOut = () => {
    forgetAffiliate();
    setToken(null);
    setData(null);
    setState("out");
  };

  return (
    <>
      <Seo
        title="Your affiliate page"
        path="/affiliate-portal"
        description="Sign in to see what you have sent us and what it has earned you."
        noIndex
      />

      <div className="shell-narrow py-12 md:py-16">
        {state === "starting" || state === "loading" ? (
          <Loading label="Opening your page" className="min-h-[40vh]" />
        ) : state === "in" ? (
          <Dashboard data={data} onSignOut={signOut} />
        ) : (
          <SignIn onSignedIn={signedIn} />
        )}
      </div>
    </>
  );
}

////////// 2. SIGNING IN //////////
// The same two ways the customer's tracking page offers, for the same reason:
// somebody who has lost one of the two still has the other. Email posts a link;
// a text sends a code, because a text with a link in it is the shape of every
// scam message anybody gets and a code cannot be used by a forwarded message.
//
// Neither path ever says whether an address or a number belongs to anybody. An
// affiliate list is a list of local businesses working with us and it is not
// ours to confirm to whoever is asking.

function SignIn({ onSignedIn }) {
  const [channel, setChannel] = useState("email");
  const [challenge, setChallenge] = useState(null);
  const [sentTo, setSentTo] = useState("");

  const ask = useForm({ identifier: "" });
  const confirm = useForm({ code: "" });

  const onAsk = ask.submit(async (values) => {
    const result = await portalApi.askForAccess({ channel, identifier: values.identifier });
    setSentTo(values.identifier);
    if (channel === "sms" && result.challenge) setChallenge(result.challenge);
    else ask.setMessage({ kind: "success", text: result.message });
    return result;
  });

  const onConfirm = confirm.submit(async (values) => {
    const result = await portalApi.verifyAccess({ challenge, code: values.code });
    onSignedIn(result.token);
    return result;
  });

  return (
    <>
      <Reveal as="p" from="none" className="eyebrow mb-4">Affiliates</Reveal>
      <Reveal as="h1" delay={60} className="display display-lg text-ink">Your page</Reveal>
      <Reveal as="p" delay={120} className="lede mt-5 text-muted">
        What you have sent us, what it earned, and your link. No password: we send you the way in.
      </Reveal>

      <Panel className="p-5 sm:p-7 mt-8">
        {challenge ? (
          <form onSubmit={onConfirm} noValidate>
            <p className="eyebrow mb-2">Step two</p>
            <h2 className="display display-sm text-ink mb-1">Enter the code</h2>
            <p className="hint mb-5">We texted a six digit code to {sentTo}. It is good for ten minutes.</p>

            <Input
              label="Six digit code"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              className="text-2xl tracking-[0.4em] text-center num"
              {...confirm.field("code")}
            />

            {confirm.message && <Alert tone="error" className="mt-4">{confirm.message.text}</Alert>}

            <div className="flex items-center gap-3 mt-6">
              <Button type="submit" loading={confirm.busy}>Open my page <ArrowRight size={16} /></Button>
              <Button type="button" variant="ghost" onClick={() => { setChallenge(null); confirm.setMessage(null); }}>
                Use something else
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={onAsk} noValidate>
            <div className="flex gap-1 mb-6">
              {[
                { value: "email", label: "Email me a link", icon: Envelope },
                { value: "sms", label: "Text me a code", icon: Message },
              ].map(({ value, label, icon: Glyph }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setChannel(value); ask.setMessage(null); }}
                  className={`btn btn-sm ${channel === value ? "btn-ink" : "btn-quiet"}`}
                >
                  <Glyph size={15} /> {label}
                </button>
              ))}
            </div>

            <Input
              key={channel}
              label={channel === "sms" ? "Mobile number we have for you" : "Email address we have for you"}
              type={channel === "sms" ? "tel" : "email"}
              autoComplete={channel === "sms" ? "tel" : "email"}
              placeholder={channel === "sms" ? "(401) 315-4653" : "you@example.com"}
              autoFocus
              {...ask.field("identifier")}
            />

            {ask.message && (
              <Alert tone={ask.message.kind === "success" ? "success" : "error"} className="mt-4">
                {ask.message.text}
              </Alert>
            )}

            <Button type="submit" loading={ask.busy} className="mt-6">
              {channel === "sms" ? "Text me a code" : "Email me a link"}
            </Button>

            <p className="hint mt-4 leading-relaxed">
              We never say whether an address or a number is on our list. If it is, the message is
              already on its way.
            </p>
          </form>
        )}
      </Panel>

      <p className="hint mt-8 text-center">
        Not signed up yet? <Link to="/affiliate-signup" className="link text-ink font-semibold">Apply here</Link>,
        or call <a href="tel:+1-401-315-4653" className="link text-ink font-semibold">(401) 315-4653</a>
        <Phone size={13} className="inline ml-1.5 -mt-0.5 text-faint" />
      </p>
    </>
  );
}

////////// 3. THE PAGE ITSELF //////////

const STATUS = {
  pending: {
    tone: "warning",
    label: "Waiting on us",
    note: "Your application is with a person. Your code does not attach to anything until it is approved, so hold off sharing the link until you hear from us. It is usually one business day.",
  },
  declined: {
    tone: "neutral",
    label: "Not active",
    note: "Your code is not active. If you think that is wrong, call us and a person will look at it.",
  },
  retired: {
    tone: "neutral",
    label: "Retired",
    note: "Your code no longer attaches to new orders. Anything already owed on the orders below is still owed.",
  },
};

function Dashboard({ data, onSignOut }) {
  const { affiliate, totals, orders } = data;
  const notice = STATUS[affiliate.status];

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Affiliate since {day(affiliate.since)}</p>
          <h1 className="display display-lg text-ink mt-2">{affiliate.company || affiliate.name}</h1>
        </div>
        <Button variant="ghost" size="sm" onClick={onSignOut}>Sign out</Button>
      </div>

      {notice && (
        <Alert tone={notice.tone} title={notice.label} className="mt-6">
          {notice.note}
        </Alert>
      )}

      {/* The link first. The single most useful thing this page does is put it
          back in front of somebody who wants to share it again. */}
      <Panel className="p-5 sm:p-6 mt-6">
        <p className="eyebrow">Your link</p>
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <code className="text-[14px] text-ink break-all">{affiliate.link}</code>
          <CopyButton value={affiliate.link} label="Copy link" />
        </div>
        <p className="hint mt-4 leading-relaxed">
          Anybody who follows it is recorded against you from that moment. Down a phone, your code
          is <strong className="text-ink font-semibold num">{affiliate.code}</strong>, and a customer
          saying it to us does the same thing.
        </p>
        <div className="mt-3">
          <CopyButton value={affiliate.code} label={`Copy ${affiliate.code}`} />
        </div>
      </Panel>

      <Panel className="grid grid-cols-2 sm:grid-cols-4 gap-5 sm:gap-6 p-5 sm:p-6 mt-5">
        <Stat label="Customers sent" value={totals.orders} />
        <Stat label="Still running" value={totals.open} />
        <Stat label="Earned so far" value={moneyExact(totals.earned)} />
        <Stat
          label="Owed to you"
          value={moneyExact(totals.owed)}
          tone={totals.owed > 0 ? "positive" : undefined}
        />
      </Panel>

      <section className="mt-8">
        <h2 className="eyebrow mb-3">Everyone you have sent</h2>
        {orders.length === 0 ? (
          <Empty icon={Clock} title="Nothing yet">
            Nobody has used your link or your code so far. It can take a few weeks: somebody has to
            ask for a kit, post their items, and accept an offer before anything is earned.
          </Empty>
        ) : (
          <Panel>
            <table className="stack-table w-full text-[14px]">
              <thead>
                <tr className="border-b border-rule">
                  <th className="text-left eyebrow font-semibold px-4 sm:px-5 py-2.5">Order</th>
                  <th className="text-left eyebrow font-semibold px-3 py-2.5">Sent</th>
                  <th className="text-left eyebrow font-semibold px-3 py-2.5">Where it is</th>
                  <th className="text-right eyebrow font-semibold px-4 sm:px-5 py-2.5">You earned</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.number} className="border-b border-rule-soft last:border-0">
                    <td data-label="Order" className="px-4 sm:px-5 py-3 num text-ink">{order.number}</td>
                    <td data-label="Sent" className="px-3 py-3 text-muted">{day(order.at)}</td>
                    <td data-label="Where it is" className="px-3 py-3">
                      {/* Closed or running, and nothing finer. Which stage of an
                          evaluation somebody's items are at is between us and
                          them. */}
                      {order.settled
                        ? <Tag tone="neutral">Finished</Tag>
                        : <Tag tone="bronze">Running</Tag>}
                    </td>
                    <td data-label="You earned" className="px-4 sm:px-5 py-3 text-right">
                      {order.commission == null ? (
                        <span className="text-faint">—</span>
                      ) : (
                        <span className="num text-ink font-medium">
                          {moneyExact(order.commission)}
                          {order.paidAt
                            ? <span className="block text-[12px] text-positive font-normal">Paid {ago(order.paidAt)}</span>
                            : <span className="block text-[12px] text-muted font-normal">Not paid yet</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )}
        <p className="hint mt-4 leading-relaxed">
          A customer's name and what they were paid are not shown here. They sold us something
          personal and did not agree to appear on anybody else's screen. The order number and the
          date are what to quote if you want to ask us about one.
        </p>
      </section>

      <p className="hint mt-8 text-center">
        Anything look wrong? Call{" "}
        <a href="tel:+1-401-315-4653" className="link text-ink font-semibold">(401) 315-4653</a> and
        a person will go through it with you.
      </p>
    </>
  );
}
